import axios from 'axios';

/**
 * Chat REST client.
 *
 * Deliberately a SEPARATE axios instance from services/api.js rather than an
 * extension of it. That client is tuned for the main site and carries two
 * behaviours that are wrong here:
 *
 *   - a global 10s timeout, too short for history paging on a slow connection
 *   - a 401 interceptor that hard-redirects to /member/login, which would throw
 *     a member out of the page they are reading because a background poll failed
 *
 * Chat runs as its own service behind /chat/api, so it also has its own base URL.
 */

const CHAT_BASE_URL = import.meta.env.VITE_CHAT_URL || '/chat/api';

const chatApi = axios.create({
  baseURL: CHAT_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  // The member session is an httpOnly cookie set by the main backend. Same
  // origin, so it rides along and no token ever touches JavaScript.
  withCredentials: true,
  timeout: 20000,
});

// Unwrap to the response body, matching the house convention in services/api.js
// so call sites read the same way across both clients.
chatApi.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const status = error.response?.status;
    const message = error.response?.data?.error || error.message || 'Request failed';

    return Promise.reject(Object.assign(new Error(message), {
      status,
      isAuthError: status === 401,
      isForbidden: status === 403,
      // Every chat route answers 503 when an administrator turns Conversations
      // off, so this is how the client learns the feature is closed.
      isUnavailable: status === 503,
    }));
  }
);

export const chatService = {
  me: () => chatApi.get('/me'),

  // Where the member keeps the Conversations window. Stored on the account so
  // it survives a new browser or a second device, not just this tab.
  getUiPrefs: () => chatApi.get('/ui-prefs'),
  updatePanelLayout: (panelLayout) => chatApi.put('/ui-prefs', { panelLayout }),

  // Conversations
  listConversations: () => chatApi.get('/conversations'),
  createConversation: (payload) => chatApi.post('/conversations', payload),
  listParticipants: (conversationId) => chatApi.get(`/conversations/${conversationId}/participants`),
  getMessages: (conversationId, params = {}) =>
    chatApi.get(`/conversations/${conversationId}/messages`, { params }),
  sendMessage: (conversationId, payload) =>
    chatApi.post(`/conversations/${conversationId}/messages`, payload),
  markRead: (conversationId, lastReadMessageId) =>
    chatApi.post(`/conversations/${conversationId}/read`, { lastReadMessageId }),
  updateConversationPrefs: (conversationId, payload) =>
    chatApi.patch(`/conversations/${conversationId}/prefs`, payload),
  addParticipants: (conversationId, memberIds) =>
    chatApi.post(`/conversations/${conversationId}/participants`, { memberIds }),
  leaveConversation: (conversationId, memberId) =>
    chatApi.delete(`/conversations/${conversationId}/participants/${memberId}`),

  // Messages
  editMessage: (messageId, body) => chatApi.patch(`/messages/${messageId}`, { body }),
  deleteMessage: (messageId) => chatApi.delete(`/messages/${messageId}`),
  getThread: (messageId) => chatApi.get(`/messages/${messageId}/thread`),
  getReactions: (messageId) => chatApi.get(`/messages/${messageId}/reactions`),
  addReaction: (messageId, emoji) => chatApi.post(`/messages/${messageId}/reactions`, { emoji }),
  removeReaction: (messageId, emoji) =>
    chatApi.delete(`/messages/${messageId}/reactions`, { params: { emoji } }),
  reportMessage: (messageId, reason) => chatApi.post(`/messages/${messageId}/report`, { reason }),

  pinConversation: (conversationId) => chatApi.post(`/conversations/${conversationId}/pin`),
  unpinConversation: (conversationId) => chatApi.delete(`/conversations/${conversationId}/pin`),

  getPins: (conversationId) => chatApi.get(`/conversations/${conversationId}/pins`),
  pinMessage: (messageId) => chatApi.post(`/messages/${messageId}/pin`),
  unpinMessage: (messageId) => chatApi.delete(`/messages/${messageId}/pin`),

  search: (q, conversationId) =>
    chatApi.get('/search', { params: { q, conversation_id: conversationId } }),

  /**
   * Send a message carrying files.
   *
   * A separate endpoint from sendMessage because the socket cannot carry binary
   * payloads and the server creates the message and its attachment rows in one
   * transaction. Content-Type is left unset so the browser supplies the
   * multipart boundary.
   */
  uploadAttachments: (conversationId, { clientMsgId, body, files, onProgress }) => {
    const form = new FormData();
    form.append('clientMsgId', clientMsgId);
    if (body) form.append('body', body);
    files.forEach((file) => form.append('files', file, file.name));

    return chatApi.post(`/conversations/${conversationId}/attachments`, form, {
      headers: { 'Content-Type': undefined },
      timeout: 120000,
      onUploadProgress: onProgress
        ? (event) => onProgress(event.total ? Math.round((event.loaded / event.total) * 100) : 0)
        : undefined,
    });
  },

  // Directory and preferences
  searchDirectory: (q) => chatApi.get('/directory', { params: { q } }),
  getNotificationPrefs: () => chatApi.get('/notification-prefs'),
  updateNotificationPrefs: (payload) => chatApi.put('/notification-prefs', payload),
  listBlocks: () => chatApi.get('/blocks'),
  block: (memberId) => chatApi.post(`/blocks/${memberId}`),
  unblock: (memberId) => chatApi.delete(`/blocks/${memberId}`),
};

/**
 * Admin surface of the chat service.
 *
 * Same axios instance, so the admin panel's httpOnly `adminToken` cookie rides
 * along on these same-origin requests — the chat service verifies it against the
 * backend's JWT_SECRET and checks the role live in the main database.
 */
export const chatAdminService = {
  overview: () => chatApi.get('/admin/overview'),

  getSettings: () => chatApi.get('/admin/settings'),
  updateSettings: (settings) => chatApi.put('/admin/settings', { settings }),

  listReports: (status = 'open') => chatApi.get('/admin/reports', { params: { status } }),
  resolveReport: (id, action, note) =>
    chatApi.post(`/admin/reports/${id}/resolve`, { action, note }),

  listSuspensions: () => chatApi.get('/admin/suspensions'),
  // Chat's own member list, so a moderator can act on someone who has not been
  // reported. Needs at least two characters — the server refuses to page the
  // whole directory.
  searchMembers: (q) => chatApi.get('/admin/members', { params: { q } }),
  setSuspension: (memberId, payload) =>
    chatApi.post(`/admin/members/${memberId}/suspension`, payload),

  listAudit: (limit = 100) => chatApi.get('/admin/audit', { params: { limit } }),

  listCronJobs: () => chatApi.get('/admin/cron/jobs'),
  updateCronJob: (key, payload) => chatApi.put(`/admin/cron/jobs/${key}`, payload),
  triggerCronJob: (key) => chatApi.post(`/admin/cron/jobs/${key}/trigger`),
  cronLogs: (job, limit = 25) => chatApi.get('/admin/cron/logs', { params: { job, limit } }),

  resyncProfiles: () => chatApi.post('/admin/maintenance/resync-profiles'),
};

export { CHAT_BASE_URL };
export default chatApi;
